'use client';
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Loader2, CheckCircle2, Droplets, Layers, Sprout } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { ancSetService, ancTierLogService, AncSet, AncTier } from '@/lib/services/ancService';
import Icon from '@/components/ui/AppIcon';


interface TierFormFields {
  setId: string;
  tier: AncTier;
  logDate: string;
  // Bottom
  leachateVolumeL: string;
  leachateColour: string;
  // Middle
  castingWeightKg: string;
  beddingCondition: string;
  moistureLevel: string;
  phReading: string;
  wormMigrationObserved: boolean;
  // Top
  feedType: string;
  feedQuantityKg: string;
  newBeddingMaterial: string;
  newBeddingQuantityKg: string;
  // Shared
  notes: string;
}

const TIER_CONFIG = {
  bottom: {
    label: 'Bottom — Water / Leachate Collection',
    icon: Droplets,
    colour: 'text-blue-500',
    bg: 'bg-blue-50 border-blue-200',
    activeBg: 'bg-blue-500',
    description: 'Collects leachate draining from upper tiers',
  },
  middle: {
    label: 'Middle — ANC Bedding & Casting',
    icon: Layers,
    colour: 'text-amber-600',
    bg: 'bg-amber-50 border-amber-200',
    activeBg: 'bg-amber-500',
    description: 'Main worm habitat — castings harvested here',
  },
  top: {
    label: 'Top — Food & New Bedding',
    icon: Sprout,
    colour: 'text-green-600',
    bg: 'bg-green-50 border-green-200',
    activeBg: 'bg-green-500',
    description: 'Fresh feed and bedding — worms migrate upward',
  },
};

const LEACHATE_COLOURS = ['Clear/light yellow', 'Dark brown', 'Amber', 'Cloudy', 'Black'];
const BEDDING_CONDITIONS = ['Excellent', 'Good', 'Needs attention', 'Overloaded', 'Too dry', 'Too wet'];
const MOISTURE_LEVELS = ['Too dry (<50%)', 'Optimal (60–80%)', 'Too wet (>80%)'];
const FEED_TYPES = ['Kitchen scraps', 'Fruit pulp', 'Vegetable waste', 'Coffee grounds', 'Paper/cardboard', 'Manure', 'Mixed organic', 'Grain bran'];
const BEDDING_MATERIALS = ['Coco coir', 'Shredded cardboard', 'Straw', 'Leaf litter', 'Peat moss', 'Mixed bedding'];

export default function AncTierForm({ onSaved }: { onSaved?: () => void }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [sets, setSets] = useState<AncSet[]>([]);
  const [selectedTier, setSelectedTier] = useState<AncTier>('top');

  useEffect(() => {
    ancSetService.getAll().then(setSets);
  }, []);

  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm<TierFormFields>({
    defaultValues: {
      logDate: new Date().toISOString().split('T')[0],
      tier: 'top',
      wormMigrationObserved: false,
    },
  });

  const watchedTier = watch('tier') as AncTier;

  useEffect(() => {
    if (watchedTier) setSelectedTier(watchedTier);
  }, [watchedTier]);

  const onSubmit = async (data: TierFormFields) => {
    if (!user) { toast.error('You must be signed in'); return; }
    if (!data.setId) { toast.error('Please select an ANC set'); return; }
    setLoading(true);
    try {
      const result = await ancTierLogService.create({
        userId: user.id,
        setId: data.setId,
        tier: data.tier,
        logDate: data.logDate,
        leachateVolumeL: data.leachateVolumeL ? parseFloat(data.leachateVolumeL) : undefined,
        leachateColour: data.leachateColour || undefined,
        castingWeightKg: data.castingWeightKg ? parseFloat(data.castingWeightKg) : undefined,
        beddingCondition: data.beddingCondition || undefined,
        moistureLevel: data.moistureLevel || undefined,
        phReading: data.phReading ? parseFloat(data.phReading) : undefined,
        wormMigrationObserved: data.wormMigrationObserved,
        feedType: data.feedType || undefined,
        feedQuantityKg: data.feedQuantityKg ? parseFloat(data.feedQuantityKg) : undefined,
        newBeddingMaterial: data.newBeddingMaterial || undefined,
        newBeddingQuantityKg: data.newBeddingQuantityKg ? parseFloat(data.newBeddingQuantityKg) : undefined,
        notes: data.notes || undefined,
      });
      if (result) {
        setSaved(true);
        toast.success('ANC tier log saved');
        setTimeout(() => setSaved(false), 3000);
        reset({ logDate: new Date().toISOString().split('T')[0], tier: data.tier, setId: data.setId, wormMigrationObserved: false });
        onSaved?.();
      } else {
        toast.error('Failed to save. Please try again.');
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save tier log');
    } finally {
      setLoading(false);
    }
  };

  const tierCfg = TIER_CONFIG[selectedTier];
  const TierIcon = tierCfg.icon;

  return (
    <div className="bg-card rounded-xl border border-border shadow-sm">
      <div className="px-4 py-3 border-b border-border">
        <h2 className="text-base font-bold text-foreground">Log Tier Activity</h2>
        <p className="text-xs text-muted-foreground mt-0.5">Logged by: {user?.user_metadata?.full_name || user?.email || 'You'}</p>
      </div>

      {saved && (
        <div className="mx-4 mt-4 flex items-center gap-2 bg-success-bg border border-success/20 rounded-lg px-3 py-2.5">
          <CheckCircle2 size={15} className="text-success" />
          <span className="text-sm font-medium text-success">Tier log saved!</span>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="p-4 space-y-4">
        {/* Set + Date */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5">
              ANC Set <span className="text-danger">*</span>
            </label>
            <select
              className={`w-full rounded-xl border px-3 py-3 text-base bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring ${errors.setId ? 'border-danger' : 'border-border'}`}
              {...register('setId', { required: 'Select a set' })}
            >
              <option value="">Select set…</option>
              {sets.map((s) => (
                <option key={s.id} value={s.id}>{s.setName}{s.location ? ` — ${s.location}` : ''}</option>
              ))}
            </select>
            {errors.setId && <p className="mt-1 text-xs text-danger">{errors.setId.message}</p>}
          </div>
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5">
              Date <span className="text-danger">*</span>
            </label>
            <input
              type="date"
              className="w-full rounded-xl border border-border px-3 py-3 text-base bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              {...register('logDate', { required: true })}
            />
          </div>
        </div>

        {/* Tier Selector */}
        <div>
          <label className="block text-sm font-semibold text-foreground mb-2">
            Tier <span className="text-danger">*</span>
          </label>
          <div className="flex flex-col gap-2">
            {(Object.entries(TIER_CONFIG) as [AncTier, typeof TIER_CONFIG['top']][]).map(([tier, cfg]) => {
              const Icon = cfg.icon;
              const isSelected = selectedTier === tier;
              return (
                <label key={tier} className="relative cursor-pointer">
                  <input type="radio" value={tier} className="sr-only" {...register('tier')} />
                  <div className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border-2 transition-all ${isSelected ? `${cfg.bg} border-current` : 'border-border bg-muted/30'}`}>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isSelected ? cfg.activeBg : 'bg-muted'}`}>
                      <Icon size={16} className={isSelected ? 'text-white' : 'text-muted-foreground'} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-semibold leading-none ${isSelected ? cfg.colour : 'text-foreground'}`}>{cfg.label}</p>
                      <p className="text-xs text-muted-foreground mt-0.5 leading-tight">{cfg.description}</p>
                    </div>
                    {isSelected && <div className={`w-2 h-2 rounded-full ${cfg.activeBg} flex-shrink-0`} />}
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        {/* Bottom Tier Fields */}
        {selectedTier === 'bottom' && (
          <div className="space-y-3 pt-1">
            <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide">Leachate / Water Collection</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Volume Collected (L)</label>
                <input
                  type="number" step="0.1" min="0"
                  className="w-full rounded-xl border border-border px-3 py-3 text-base bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="e.g. 3.5"
                  {...register('leachateVolumeL')}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Leachate Colour</label>
                <select
                  className="w-full rounded-xl border border-border px-3 py-3 text-base bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  {...register('leachateColour')}
                >
                  <option value="">Not noted</option>
                  {LEACHATE_COLOURS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Middle Tier Fields */}
        {selectedTier === 'middle' && (
          <div className="space-y-3 pt-1">
            <p className="text-xs font-semibold text-amber-600 uppercase tracking-wide">ANC Bedding & Casting</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Castings Collected (kg)</label>
                <input
                  type="number" step="0.1" min="0"
                  className="w-full rounded-xl border border-border px-3 py-3 text-base bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="e.g. 5.0"
                  {...register('castingWeightKg')}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">pH Reading</label>
                <input
                  type="number" step="0.1" min="4" max="10"
                  className="w-full rounded-xl border border-border px-3 py-3 text-base bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="e.g. 7.0"
                  {...register('phReading')}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Bedding Condition</label>
                <select
                  className="w-full rounded-xl border border-border px-3 py-3 text-base bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  {...register('beddingCondition')}
                >
                  <option value="">Not checked</option>
                  {BEDDING_CONDITIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Moisture Level</label>
                <select
                  className="w-full rounded-xl border border-border px-3 py-3 text-base bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  {...register('moistureLevel')}
                >
                  <option value="">Not checked</option>
                  {MOISTURE_LEVELS.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            </div>
            <label className="flex items-center gap-3 px-3 py-3 rounded-xl border border-border bg-muted/30 cursor-pointer">
              <input type="checkbox" className="w-4 h-4 rounded accent-primary" {...register('wormMigrationObserved')} />
              <div>
                <p className="text-sm font-semibold text-foreground">Worm migration observed</p>
                <p className="text-xs text-muted-foreground">Worms moving upward toward top tier</p>
              </div>
            </label>
          </div>
        )}

        {/* Top Tier Fields */}
        {selectedTier === 'top' && (
          <div className="space-y-3 pt-1">
            <p className="text-xs font-semibold text-green-600 uppercase tracking-wide">Food & New Bedding</p>
            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5">Feed Type</label>
              <select
                className="w-full rounded-xl border border-border px-3 py-3 text-base bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                {...register('feedType')}
              >
                <option value="">Select feed type…</option>
                {FEED_TYPES.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Feed Quantity (kg)</label>
                <input
                  type="number" step="0.1" min="0"
                  className="w-full rounded-xl border border-border px-3 py-3 text-base bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="e.g. 2.5"
                  {...register('feedQuantityKg')}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">New Bedding (kg)</label>
                <input
                  type="number" step="0.1" min="0"
                  className="w-full rounded-xl border border-border px-3 py-3 text-base bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="e.g. 1.0"
                  {...register('newBeddingQuantityKg')}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5">New Bedding Material</label>
              <select
                className="w-full rounded-xl border border-border px-3 py-3 text-base bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                {...register('newBeddingMaterial')}
              >
                <option value="">None added</option>
                {BEDDING_MATERIALS.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
          </div>
        )}

        {/* Notes */}
        <div>
          <label className="block text-sm font-semibold text-foreground mb-1.5">Notes</label>
          <textarea
            rows={2}
            className="w-full rounded-xl border border-border px-3 py-3 text-base bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
            placeholder="Observations, concerns, or actions taken…"
            {...register('notes')}
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full py-4 rounded-xl bg-primary text-primary-foreground font-bold text-base hover:opacity-90 transition-all active:scale-95 disabled:opacity-70 flex items-center justify-center gap-2"
        >
          {loading ? <><Loader2 size={18} className="animate-spin" /> Saving…</> : '✓ Save Tier Log'}
        </button>
      </form>
    </div>
  );
}
